import _ from 'lodash';
import React from 'react';
import styled, { ThemeContext } from 'styled-components';
import ScrollTransition from '../../shared/scroll-transition';
import propTypes from '../common/prop-types';
import { DesktopSeparator, ContentContainer } from '../../shared/sectionable/styles';
import EnquiryContext, { reducer, defaultState } from '../../shared/enquiry/context';
import useFundStateStore from '../common/fund-state-hook';
import PrimaryContent from './primary-content';
import Meta from './meta';
import FeaturedWires from '../../shared/sectionable/featured-wires';
import Info from './info';
import Performance from './performance';
import FeesAndPricing from './fees-and-pricing';
import Qanda from '../../shared/sectionable/qanda';
import PreviewMode from '../../shared/sticky-preview-mode';
import FundUpdates from './fund-updates';
import LatestWires from '../../contributors/contributor-components/latest-wires';
import Invest from './invest';
import Disclaimer from './disclaimer';
import StickyFooter from './sticky-footer';
import StickyHeader from './sticky-header';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  ${props => props.theme.isMobileOrTablet && `
    width: 100%;
  `};
  ${props => props.theme.isLarge && `
    width: 100%;
    padding: 0 32px;
  `};
  ${props => props.theme.isXLarge && `
    width: 953px;
    padding: 0;
  `};
  ${props => props.theme.isXXLarge && `
    width: 1200px;
    padding: 0;
  `};
`;

const MarginDesktopSeparator = styled(DesktopSeparator)`
  margin-bottom: 72px;
`;

const checkIfShowPerf = f => {
  const date = _.get(f, 'inceptionDate');
  if (!date) {
    return false;
  }
  const perfList = _.get(f, 'performance', []);
  if (!perfList.length) {
    return false;
  }
  const valids = _.filter(perfList, p => !Number.isNaN(parseFloat(_.get(p, 'value'))));
  return valids.length >= 2 && _.some(valids, p => p.name === 'returnSinceInceptionPercent');
};

const checkIfShowFees = f => !!f.managementFee;

// eslint-disable-next-line complexity
export default function Fund(props) {
  const {
    fund, showInModal, newNavEnabled, viewAllWiresLink,
  } = props;
  const { published, activePartnership } = fund;
  const { isLarge } = React.useContext(ThemeContext);
  const [yOffset, setYOffset] = React.useState(0);
  const [eState, dispatch] = React.useReducer(reducer, defaultState);
  const set = useFundStateStore(state => state.set);
  const fundManager = _.find(fund.members, { role: 'manager' });

  React.useEffect(() => {
    set(state => {
      state.followed = fund.isFollowedByCurrentUser;
    });
  }, [set, fund.isFollowedByCurrentUser]);

  const qandaProps = {
    fund: _.pick(fund, ['id', 'name', 'qandasUpdatedAt']),
    manager: fundManager,
    livewireLogo: props.livewireLogo,
  };

  const fundUpdatesProps = {
    fund: _.pick(fund, ['id', 'name', 'monthlyUpdateFileUrl', 'isFollowedByCurrentUser']),
  };

  const showInvestSection = !!activePartnership;
  const showPerf = checkIfShowPerf(fund);
  const showFees = checkIfShowFees(fund);
  const showContactButton = !!activePartnership;

  const stickyProps = {
    fund,
    showContactButton,
    yOffset,
    newNavEnabled,
  };

  const offsetPreviewMode = !isLarge && yOffset > 250 ? 47 : 0;

  return (
    <Container>
      <ScrollTransition onScroll={setYOffset} />
      {!published && <PreviewMode yOffset={offsetPreviewMode} />}
      <EnquiryContext.Provider value={{ state: eState, dispatch }}>
        {!!isLarge && !showInModal && <StickyHeader {...stickyProps} />}
        <PrimaryContent
          fund={fund}
          showInModal={showInModal}
          showContactButton={showContactButton}
        />
        <Meta fund={fund} />
        <ContentContainer>
          <FeaturedWires objectId={fund.id} type="fund" />
          <Info fund={fund} />
          {!!showPerf && <Performance fund={fund} />}
          {showFees && <FeesAndPricing fund={fund} />}
          <Qanda {...qandaProps} />
          {!!fundUpdatesProps.fund.monthlyUpdateFileUrl && (
            <>
              <DesktopSeparator />
              <FundUpdates {...fundUpdatesProps} />
            </>
          )}
          <>
            <MarginDesktopSeparator />
            <LatestWires profile={fund} viewAllWiresLink={viewAllWiresLink} type="fund" />
          </>
          {!!showInvestSection && <Invest {...props} />}
          <Disclaimer disclaimer={fund.disclaimer} />
          <Disclaimer lw />
        </ContentContainer>
        {!isLarge && !showInModal && <StickyFooter {...stickyProps} />}
      </EnquiryContext.Provider>
    </Container>
  );
}
// eslint-enable-next-line complexity

Fund.propTypes = propTypes;
